/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { useNavigate } from "react-router";
import { Box, useTheme, useMediaQuery, Button, Typography, Modal, FormHelperText, IconButton } from "@mui/material";
import "@styles/stylesRegistro.css";
import { PhoneCodeInput } from "@components/PhoneCodeInput";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { STATUS_CATALOG } from "@helpers/constantes";
import {
  BuroValidatorRequestPFAE,
  CiecValidatorRequest,
  useBuroValidatorMutation,
  useCiecValidatorMutation,
} from "@api/sat_apis_new/satApsi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { setModalErrorVisible, setProcessPage } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { setCheckSatBureau } from "@store/slices/registerSlice";
import CloseIcon from "@mui/icons-material/Close";
import useStatusConfig from "@hooks/useStatusConfig";
import { truncateText } from "@helpers/funciones";

interface ModalBuroNipProps {
  screenStatus: string;
  open: boolean;
  onClose: () => void;
}

const ModalBuroNip = (props: ModalBuroNipProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(props.screenStatus);

  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const legalRepresentative = useSelector((state: RootState) => state.register.legal_representative);
  const brand = useSelector((state: RootState) => state.brand);

  const [confirmCode, setConfirmCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [ciecValidation] = useCiecValidatorMutation();
  const [buroValidator] = useBuroValidatorMutation();

  const continueToWaitingRoom = async () => {
    try {
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setProcessPage(STATUS_CATALOG.waiting_room.url));
      dispatch(setCheckSatBureau({ check_sat_bureau: true }));
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "ConsultaBuro",
          values: {},
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const handleClickConfirm = async () => {
    try {
      setIsLoading(true);

      if (!legalRepresentative) {
        throw new Error("No se encontró el representante legal");
      }

      const legal_name = truncateText(legalRepresentative.name || "", 26);
      const first_name = truncateText(legalRepresentative.name || "", 26);
      const first_lastname = truncateText(legalRepresentative.last_name || "", 26);
      const second_lastname = truncateText(legalRepresentative.last_name_2 || "", 26);

      const payload_ciec: CiecValidatorRequest = {
        rfc: legalRepresentative.rfc || "",
        ciec: "",
        application_code: application.company_application_code,
        client_details_id: String(application.client_details_id),
        loan_application_conditions_id: String(application.application_condition_id),
        loan_application_id: String(application.id),
        requested_amount: Number(application.loan_amount),
        requested_term: Number(application.loan_term),
        validate_ciec: false,
        company_id: brand?.id ?? "",
        connection_is_mobile_app: true,
        person_id: legalRepresentative.id,
      };

      await ciecValidation(payload_ciec).unwrap();

      const payload_buro: BuroValidatorRequestPFAE = {
        person_Type: "pf",
        data: {
          rfc: legalRepresentative.rfc,
          transaction_number: application.id,
          legal_name: legal_name,
          first_name: first_name,
          second_name: "",
          father_last_name: first_lastname,
          mother_last_name: second_lastname,
          reenter_NIP: true,
          authorization_label_response: true,
          loan_application_id: application.id,
          is_endorsement: false,
          person_id: legalRepresentative.id,
        },
      };

      await buroValidator(payload_buro).unwrap();

      await continueToWaitingRoom();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 409) {
        await continueToWaitingRoom();
      } else {
        Sentry.captureException(error, {
          extra: {
            component: "ConsultaBuroNipPF",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={props.open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          width: "70%",
          maxWidth: "400px",
          outline: "none",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography fontWeight={"600"} gutterBottom color="primary.dark">
          Consulta a Buró de crédito
        </Typography>

        <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ mt: 2, mb: 2 }} color="primary.dark">
          <CustomTextCiecBuro type="nipBuroConfirm" />
        </Typography>

        <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ mt: 2, mb: 2 }} color="primary.dark">
          Confirma tu Código
        </Typography>

        <PhoneCodeInput
          name="code_confirm"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmCode(event.target.value);
          }}
          disabled={isLoading}
        />

        {confirmCode.length === 4 && confirmCode !== buroNip.code && (
          <FormHelperText sx={{ mt: 1 }}>
            El código ingresado no es correcto. Por favor verifica el código y vuelve a intentarlo.
          </FormHelperText>
        )}

        <Button
          variant="default"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleClickConfirm}
          disabled={confirmCode !== buroNip.code || isLoading}
        >
          Confirmar
        </Button>
      </Box>
    </Modal>
  );
};

export default ModalBuroNip;
